import React from 'react';

/**
 * Light-weight skeleton loader primitives. Used instead of plain
 * "טוען..." text so the page reserves space for the eventual content
 * and the wait feels intentional, not broken.
 *
 * Inline keyframes — duplicate <style> tags get deduped by React.
 */

const SHIMMER_CSS = `
@keyframes huri-shimmer {
  0%   { background-position: -240px 0; }
  100% { background-position: 240px 0; }
}
.huri-skeleton {
  background: linear-gradient(90deg, #eef0f3 0%, #f7f8fa 50%, #eef0f3 100%);
  background-size: 480px 100%;
  animation: huri-shimmer 1.4s ease-in-out infinite;
  border-radius: 6px;
}
`;

function SkeletonStyle() {
  return <style>{SHIMMER_CSS}</style>;
}

export function SkeletonBox({ width = '100%', height = '1rem', style }) {
  return (
    <>
      <SkeletonStyle />
      <span
        className="huri-skeleton"
        style={{ display: 'block', width, height, ...style }}
      />
    </>
  );
}

export function SkeletonStatsRow({ cards = 3 }) {
  return (
    <>
      <SkeletonStyle />
      <div className="stats-row" aria-busy="true" aria-label="טוען נתונים">
        {Array.from({ length: cards }).map((_, i) => (
          <div key={i} className="stat-card">
            <SkeletonBox width="40%" height="1.6rem" style={{ marginBottom: '0.5rem' }} />
            <SkeletonBox width="55%" height="0.85rem" />
          </div>
        ))}
      </div>
    </>
  );
}

export function SkeletonTable({ rows = 5, columns = 5 }) {
  return (
    <>
      <SkeletonStyle />
      <div className="data-table" aria-busy="true" aria-label="טוען טבלה">
        <table>
          <thead>
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i}><SkeletonBox width="70%" height="0.85rem" /></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, r) => (
              <tr key={r}>
                {Array.from({ length: columns }).map((_, c) => (
                  <td key={c}><SkeletonBox width="80%" height="0.85rem" /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function SkeletonMap({ height = 420 }) {
  return (
    <>
      <SkeletonStyle />
      <div
        className="huri-skeleton"
        aria-busy="true"
        aria-label="טוען מפה"
        style={{
          width: '100%',
          height,
          borderRadius: 8,
          marginBottom: '1rem',
        }}
      />
    </>
  );
}

export function SkeletonList({ rows = 4 }) {
  return (
    <>
      <SkeletonStyle />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }} aria-busy="true">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            style={{
              padding: '0.75rem 0.9rem',
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
            }}
          >
            <SkeletonBox width="40%" height="0.95rem" style={{ marginBottom: '0.5rem' }} />
            <SkeletonBox width="80%" height="0.8rem" />
          </div>
        ))}
      </div>
    </>
  );
}

export function SkeletonCard({ lines = 3 }) {
  return (
    <>
      <SkeletonStyle />
      <div
        className="info-card"
        aria-busy="true"
        style={{ padding: '1rem' }}
      >
        <SkeletonBox width="35%" height="1.1rem" style={{ marginBottom: '0.85rem' }} />
        {Array.from({ length: lines }).map((_, i) => (
          <SkeletonBox
            key={i}
            width={i === lines - 1 ? '70%' : '100%'}
            height="0.85rem"
            style={{ marginBottom: '0.55rem' }}
          />
        ))}
      </div>
    </>
  );
}

export default SkeletonBox;
